import { DidomiSDK } from '@didomi/react';
import { RulesKeys } from 'app/modules/rules/domain/rules.keys';
import { RulesContext } from 'app/modules/rules/domain/RulesContext';
import { getAppDataConf } from 'app/modules/rules/service/rulesService';
import { getNavItemsMobile } from 'app/modules/rules/service/store/selectors';
import MobileRoutes from 'app/pages/mobile/mobile.routes';
import { IRootState } from 'app/shared/reducers';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader';
import { useDispatch, useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import { getDocCMS } from './modules/cms/service/cmsService';
import { getAvatar, getGlobalConf, setDeviceType } from './modules/global/service/globalService';
import { getUser } from './modules/user/service/userService';
import { didomiConfig } from './shared/config/didomi';

import DesktopRoutes from 'app/pages/desktop/desktop.routes';
import chat from 'app/shared/util/chat-widget';
import './app.scss';
import { getAvatarList, isReady } from './modules/cms/service/store/selectors';
import { DeviceType } from './modules/rules/domain/Device.type';
import ErrorBoundary from './shared/error/error-boundary';

export const DidomiContext = React.createContext(null);

export const App = () => {


  const appStyle: any = useSelector<IRootState>(state => state.cms.documents['app_global_type']);
  const isGlobalReady: any = useSelector<IRootState>(state => state.global.isReady);
  const isReadyRules: any = useSelector<IRootState>(state => state.rules.isReady);
  const drulesConfig = useSelector(getNavItemsMobile(RulesKeys.GET_CONF));
  const configuration: any = useSelector<IRootState>(state => state.global.configuration);
  const [didomiObject, setDidomiObject] = useState(null);
  const cmsIsReady: any = useSelector<IRootState>(isReady);
  const avatarListCms: any = useSelector<IRootState>(getAvatarList);
  const lstAvatars: any = useSelector<IRootState>(state => state.global.lstAvatars);
  const avatar: any = useSelector<IRootState>(state => state.global.avatar);
  const dispatch = useDispatch();

  useEffect(() => {
    if (drulesConfig && configuration) {
      if (drulesConfig?.settings?.chat !== undefined && drulesConfig?.settings?.chat === true) {
        chat(configuration);
      }
    }
  }, [drulesConfig, configuration]);

  useEffect(() => {

    dispatch(setDeviceType(isMobile ? DeviceType.MOBILE : DeviceType.DESKTOP))
  }, [drulesConfig]);

  useEffect(() => {
    dispatch(getUser());
    dispatch(getDocCMS('app_global_type', true));
    dispatch(getDocCMS('avatar_information'));
    dispatch(getDocCMS('modal_informations'));

    dispatch(getGlobalConf());
  }, []);

  useEffect(() => {
    let rulesContext: RulesContext = { key: RulesKeys.GET_CONF }
    switch (configuration.device) {
      case DeviceType.ALL: {

        rulesContext = {
          key: RulesKeys.GET_CONF,
          entryDataRules: { deviceType: isMobile ? DeviceType.MOBILE : DeviceType.DESKTOP },
        };
        dispatch(getAppDataConf(rulesContext));
      }
        break;
      case DeviceType.DESKTOP: {

        rulesContext = {
          key: RulesKeys.GET_CONF,
          entryDataRules: { deviceType: DeviceType.DESKTOP },
        };
        dispatch(getAppDataConf(rulesContext));
      }
        break;
      case DeviceType.MOBILE: {

        rulesContext = {
          key: RulesKeys.GET_CONF,
          entryDataRules: { deviceType: DeviceType.MOBILE },
        };
        dispatch(getAppDataConf(rulesContext));

      }
        break;
      default: {

        rulesContext = {
          key: RulesKeys.GET_CONF,
          entryDataRules: { deviceType: DeviceType.MOBILE },
        };

      }
    }
  }, [configuration])

  const onDidomiReady = didomi => {
    setDidomiObject(didomi);
  };

  useEffect(() => {
    if (configuration?.activeOutilGermain === true){
      const url = configuration?.serverUrlGermain + "?defaultProfileName="+configuration?.monitoringProfilName+"&appName="+configuration?.appName; 
    // Créer une balise <script> dynamiquement
    const script = document.createElement('script');
    script.src = url;
    // Ajouter la balise <script> au document
    document.body.appendChild(script);
    // Nettoyer après le démontage du composant
    return () => {
      document.body.removeChild(script);
    };
    }

  }, [configuration?.activeOutilGermain]);

  useEffect(() => {
    if (avatarListCms && lstAvatars?.length === 0) {
      dispatch(getAvatar(avatarListCms));
    }
  }, [avatarListCms, lstAvatars]);

  useEffect(() => {
    if (avatar?.image) {
      document.documentElement.style.setProperty('--product_chat_icon', 'url(' + avatar?.image + ')');
    }
  }, [avatar]);

  return (
    <DidomiContext.Provider value={didomiObject}>
      <Helmet>
        <link rel="icon" href={'/content/images/navbar/spb-mini-icon.png'} />
        <title>{appStyle?.data['title'] || 'Assurances SPB - Espace utilisateur'}</title>
      </Helmet>

      {isReadyRules === true && cmsIsReady === true && configuration ? (
        drulesConfig?.settings?.deviceType && drulesConfig?.settings?.deviceType === 'MOBILE' ? (
          <ErrorBoundary>
            <MobileRoutes brandImage={appStyle?.data?.main_logo?.url} />
          </ErrorBoundary>
        ) : (
          <ErrorBoundary>
            <DesktopRoutes />
          </ErrorBoundary>
        )
      )
        : (
          <div></div>
        )}

      {isGlobalReady && configuration && (
        <DidomiSDK
          config={didomiConfig(configuration.publicKeyDidomi, configuration.productCode)}
          iabVersion={2} // If you want to support the TCF v1∏, don't forget to change this value, even if you selected the TCF v2 in the console. This parameter will load the correct stub in the React Component
          noticeId={configuration.noticeIdDidomi} // If you want to target the notice by ID and not by domain
          gdprAppliesGlobally={true}
          sdkPath="https://sdk.privacy-center.org/"
          embedTCFStub={true}
          onReady={onDidomiReady}
          onConsentChanged={cwtToken => console.log('A consent has been given/withdrawn', cwtToken)}
          onNoticeShown={() => console.log('Didomi Notice Shown')}
          onNoticeHidden={() => console.log('Didomi Notice Hidden')}
        />
      )}
    </DidomiContext.Provider>
  );
};

export default hot(module)(App);
